<template>
    <div class="ui-page">
        <ui-page-header title="Painel" subtitle="Números, pendências de cadastro e próximos marcos do processo selecionado.">
            <competitive-process-picker v-model="processId" />
        </ui-page-header>

        <ui-empty v-if="!processId && !loading" icon="mdi-bank-outline" text="Cadastre um processo para ver o painel" />
        <div v-else-if="loading && !data" class="ui-empty"><v-progress-circular indeterminate size="28" color="primary"></v-progress-circular></div>
        <template v-else-if="data">
            <div class="cp-kpis">
                <div class="cp-kpi"><div class="cp-kpi__head">Ativos<v-icon small>mdi-map-marker-outline</v-icon></div><div class="cp-kpi__value">{{ data.kpis.assets }}</div><div class="cp-kpi__hint">{{ data.kpis.cities }} {{ data.kpis.cities === 1 ? "município" : "municípios" }}</div></div>
                <div class="cp-kpi"><div class="cp-kpi__head">Valor de avaliação<v-icon small>mdi-bank-outline</v-icon></div><div class="cp-kpi__value">{{ formatMoneyShort(data.kpis.appraisal_total) }}</div><div class="cp-kpi__hint">Soma dos laudos</div></div>
                <div class="cp-kpi"><div class="cp-kpi__head">Inscrições<v-icon small>mdi-account-check-outline</v-icon></div><div class="cp-kpi__value">{{ data.kpis.registrations }}</div>
                    <div class="cp-kpi__hint">{{ data.kpis.registrations_approved }} {{ data.kpis.registrations_approved === 1 ? "habilitado" : "habilitados" }} · {{ data.kpis.registrations_submitted }} em análise</div></div>
                <div class="cp-kpi"><div class="cp-kpi__head">Indisponíveis<v-icon small>mdi-lock-outline</v-icon></div><div class="cp-kpi__value">{{ data.kpis.blocked_assets }}</div><div class="cp-kpi__hint">ativos bloqueados</div></div>
                <div class="cp-kpi"><div class="cp-kpi__head">Etapas<v-icon small>mdi-format-list-numbered</v-icon></div><div class="cp-kpi__value">{{ data.kpis.stages }}</div>
                    <div class="cp-kpi__hint">{{ stageHint }}</div></div>
                <div class="cp-kpi"><div class="cp-kpi__head">Arrematados<v-icon small>mdi-trophy-outline</v-icon></div><div class="cp-kpi__value">{{ data.kpis.sold_assets }}</div><div class="cp-kpi__hint">{{ data.kpis.sold_assets ? "ativos" : "Nenhum arremate homologado" }}</div></div>
            </div>

            <div class="cp-notice">
                <v-icon small>mdi-shield-lock-outline</v-icon>
                <span><strong>Sigilo das propostas.</strong> Os envelopes lacrados não revelam valor nem o item pretendido antes da sessão pública de abertura: o painel vai mostrar apenas quantos foram recebidos. Urna e sessão chegam nas próximas fases.</span>
            </div>

            <div class="cp-grid-2">
                <ui-section title="O que exige ação" description="Pendências de cadastro do processo selecionado.">
                    <ul class="cp-actions">
                        <li v-for="item in pendingSorted" :key="item.key" class="cp-action" role="button" tabindex="0" @click="goPending(item)" @keydown.enter="goPending(item)">
                            <span class="cp-action__icon"><v-icon small>{{ PENDING_ICONS[item.key] || "mdi-alert-circle-outline" }}</v-icon></span>
                            <span class="cp-action__text">
                                <span class="ui-cell-main d-block">{{ item.label }}</span>
                                <span class="ui-cell-sub d-block">{{ item.description }}</span>
                            </span>
                            <ui-badge v-if="item.count" :text="String(item.count)" tone="warning" :dot="false" />
                            <ui-badge v-else text="Em dia" tone="neutral" :dot="false" />
                            <v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon>
                        </li>
                    </ul>
                </ui-section>
                <ui-section title="Próximos marcos" description="Datas do cronograma, em horário de Brasília.">
                    <ul v-if="data.milestones.length" class="cp-milestones">
                        <li v-for="(item, index) in data.milestones" :key="`${item.at}-${index}`" class="cp-milestone" :class="{ 'cp-milestone--next': index === 0 }">
                            <div class="cp-milestone__when">{{ formatSchedule(item.at) }} · {{ relativeDays(item.at) }}</div>
                            <div class="cp-milestone__label">{{ item.label }}</div>
                        </li>
                    </ul>
                    <p v-else class="ui-small ui-muted mb-0">Nenhum marco futuro no cronograma.</p>
                </ui-section>
            </div>

            <ui-table-card>
                <template #toolbar>
                    <div>
                        <div class="ui-section-title">Etapas do processo</div>
                        <div class="ui-section-description">Cada etapa oferta um tipo de agrupamento com o seu percentual mínimo sobre a avaliação.</div>
                    </div>
                    <span class="ui-toolbar__spacer"></span>
                    <v-btn text color="primary" :to="{ path: '/processo-competitivo/etapas', query: { processo: String(processId) } }">Gerenciar etapas</v-btn>
                </template>
                <v-data-table :headers="stageHeaders" :items="data.stages" disable-sort disable-pagination hide-default-footer no-data-text="Nenhuma etapa cadastrada">
                    <template v-slot:[`item.name`]="{ item }">
                        <div class="ui-cell-main">{{ item.number }}. {{ item.name }}</div>
                        <div class="ui-cell-sub">{{ item.offer_kind_label }} · mínimo {{ formatPercent(item.minimum_percent) }} · recebimento {{ formatSchedule(item.receiving_starts_at) }} a {{ formatSchedule(item.receiving_ends_at) }}</div>
                    </template>
                    <template v-slot:[`item.items`]="{ item }">{{ item.items }} {{ item.items === 1 ? "item" : "itens" }}<span v-if="item.blocked_items" class="cp-warn"> · {{ item.blocked_items }} {{ item.blocked_items === 1 ? "bloqueado" : "bloqueados" }}</span></template>
                    <template v-slot:[`item.status`]="{ item }"><ui-badge set="cp-stage" :status="item.status" /></template>
                </v-data-table>
            </ui-table-card>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatMoneyShort, formatPercent, formatSchedule, notifyError, nowBrasilia, relativeDays } from "@/components/competitive/competitive.util";

const PENDING_ICONS = {
    registrations_review: "mdi-account-clock-outline",
    required_documents: "mdi-file-document-multiple-outline",
    nda: "mdi-file-lock-outline",
    notice: "mdi-file-document-outline",
    appraisal_value: "mdi-cash-remove",
    appraisal_file: "mdi-file-alert-outline",
    coordinates: "mdi-map-marker-off-outline",
    media: "mdi-image-off-outline",
    stage_schedule: "mdi-calendar-alert",
    stage_items: "mdi-format-list-checks",
    custody_keys: "mdi-key-alert-outline",
    urn_seal: "mdi-lock-open-alert-outline",
    share_withdrawal: "mdi-key-chain-variant",
    timestamps_failed: "mdi-clock-alert-outline",
    homologations_pending: "mdi-gavel",
};
// Pendencia -> tela que resolve (com o filtro de ativos quando houver).
const PENDING_ROUTES = {
    registrations_review: (id) => ({ path: "/processo-competitivo/inscricoes", query: { processo: String(id), situacao: "submitted" } }),
    required_documents: (id) => ({ path: `/processo-competitivo/processos/${id}`, query: { aba: "habilitacao" } }),
    nda: (id) => ({ path: `/processo-competitivo/processos/${id}`, query: { aba: "habilitacao" } }),
    notice: (id) => ({ path: `/processo-competitivo/processos/${id}` }),
    appraisal_value: (id) => ({ path: "/processo-competitivo/ativos", query: { processo: String(id), sem: "appraisal" } }),
    appraisal_file: (id) => ({ path: "/processo-competitivo/ativos", query: { processo: String(id), sem: "file" } }),
    coordinates: (id) => ({ path: "/processo-competitivo/ativos", query: { processo: String(id), sem: "coordinates" } }),
    media: (id) => ({ path: "/processo-competitivo/ativos", query: { processo: String(id), sem: "media" } }),
    stage_schedule: (id) => ({ path: "/processo-competitivo/etapas", query: { processo: String(id) } }),
    stage_items: (id) => ({ path: "/processo-competitivo/etapas", query: { processo: String(id) } }),
    custody_keys: (id) => ({ path: `/processo-competitivo/processos/${id}`, query: { aba: "custodia" } }),
    urn_seal: (id) => ({ path: `/processo-competitivo/processos/${id}`, query: { aba: "custodia" } }),
    share_withdrawal: (id) => ({ path: `/processo-competitivo/processos/${id}`, query: { aba: "custodia" } }),
    timestamps_failed: (id) => ({ path: "/processo-competitivo/trilha", query: { processo: String(id) } }),
    homologations_pending: (id) => ({ path: "/processo-competitivo/homologacao", query: { processo: String(id) } }),
};

export default {
    name: "CompetitiveDashboard",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        PENDING_ICONS,
        data: null,
        loading: false,
        stageHeaders: [
            { text: "Etapa", value: "name" },
            { text: "Itens", value: "items", width: 200 },
            { text: "Situação", value: "status", width: 130 },
        ],
    }),
    computed: {
        // Pendencias abertas primeiro.
        pendingSorted() {
            return [...this.data.pending].sort((a, b) => (b.count > 0) - (a.count > 0));
        },
        // Etapa vigente ou, antes de abrir o recebimento, a proxima.
        stageHint() {
            const stage = this.data.kpis.current_stage;
            if (!stage) return "Nenhuma etapa pela frente";
            const started = !stage.receiving_starts_at || String(stage.receiving_starts_at).slice(0, 19) <= nowBrasilia();
            return `${started ? "Vigente" : "Próxima"}: ${stage.number} · ${stage.name}`;
        },
    },
    methods: {
        formatMoneyShort,
        formatPercent,
        formatSchedule,
        relativeDays,
        onProcess() {
            this.load();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/overview`);
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        goPending(item) {
            const route = PENDING_ROUTES[item.key];
            if (route) this.$router.push(route(this.processId));
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
